'use client';

import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Alert01Icon, Calendar03Icon } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { Link } from '@/i18n/navigation';

const LOADING_MARKET_IDS: readonly string[] = ['first', 'second', 'third'];

interface EventDetailErrorProps {
  onRetry: () => void;
  onBack?: (() => void) | undefined;
  backHref?: string | undefined;
}

interface EventDetailNotFoundProps {
  onBack?: (() => void) | undefined;
  backHref?: string | undefined;
}

export function EventDetailLoading(): JSX.Element {
  const t = useTranslations('liveEvents.eventDetailBoundary');

  return (
    <output
      aria-busy="true"
      data-testid="event-detail-loading"
      className="flex min-w-0 w-full flex-col @container/event-detail-loading"
    >
      <span className="sr-only">{t('loading')}</span>
      <Panel
        data-testid="event-detail-loading-hero"
        density="flush"
        className="h-75 overflow-hidden @max-[767px]/event-detail-loading:h-92"
      >
        <PanelContent className="flex h-full flex-col justify-between p-5 @max-[767px]/event-detail-loading:p-4">
          <div className="flex items-center justify-between gap-4" aria-hidden="true">
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-18 rounded-pill" />
              <Skeleton className="h-6 w-22 rounded-pill" />
            </div>
            <Skeleton className="size-9 rounded-full" />
          </div>
          <div className="grid gap-3" aria-hidden="true">
            <Skeleton className="h-10 w-2/3 max-w-xl" />
            <Skeleton className="h-5 w-2/5 max-w-sm" />
            <div className="flex flex-wrap gap-2">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-28" />
            </div>
          </div>
        </PanelContent>
      </Panel>

      <Panel density="flush" className="mt-4 h-28" aria-hidden="true">
        <PanelContent className="flex h-full items-center justify-between gap-4 p-4">
          <div className="flex min-w-0 items-center gap-3">
            <Skeleton className="size-12 shrink-0 rounded-item" />
            <div className="grid gap-2">
              <Skeleton className="h-5 w-44" />
              <Skeleton className="h-4 w-28" />
            </div>
          </div>
          <div className="grid justify-items-end gap-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-8 w-32" />
          </div>
        </PanelContent>
      </Panel>

      <section className="mt-6 min-w-0" aria-hidden="true">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <Skeleton className="h-7 w-28" />
          <div className="flex flex-wrap gap-2">
            <Skeleton className="h-9 w-28 rounded-pill" />
            <Skeleton className="h-9 w-36 rounded-pill" />
          </div>
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,22rem),1fr))] gap-4">
          {LOADING_MARKET_IDS.map((id) => (
            <Panel
              key={id}
              data-testid="event-detail-loading-market"
              density="compact"
              className="h-64"
            >
              <PanelContent className="flex h-full flex-col gap-4">
                <Skeleton className="h-6 w-4/5" />
                <Skeleton className="h-8 w-2/3 rounded-pill" />
                <Skeleton className="h-8 w-1/2 rounded-pill" />
                <Skeleton className="mt-auto h-4 w-full" />
                <Skeleton className="h-4 w-3/5" />
              </PanelContent>
            </Panel>
          ))}
        </div>
      </section>
    </output>
  );
}

export function EventDetailError({
  onRetry,
  onBack,
  backHref,
}: EventDetailErrorProps): JSX.Element {
  const t = useTranslations('liveEvents.eventDetailBoundary');

  return (
    <EmptyState
      data-testid="event-detail-error"
      framed
      size="page"
      icon={Alert01Icon}
      title={t('errorTitle')}
      description={t('errorDescription')}
      action={
        <div className="flex flex-wrap justify-center gap-2">
          <AnimatedButton onClick={onRetry}>{t('retry')}</AnimatedButton>
          {backHref === undefined ? (
            <AnimatedButton variant="secondary" onClick={onBack}>
              {t('backToFeed')}
            </AnimatedButton>
          ) : (
            <AnimatedButton variant="secondary" asChild>
              <Link href={backHref}>{t('backToFeed')}</Link>
            </AnimatedButton>
          )}
        </div>
      }
    />
  );
}

export function EventDetailNotFound({ onBack, backHref }: EventDetailNotFoundProps): JSX.Element {
  const t = useTranslations('liveEvents.eventDetailBoundary');

  return (
    <EmptyState
      data-testid="event-detail-not-found"
      size="page"
      icon={Calendar03Icon}
      title={t('notFoundTitle')}
      description={t('notFoundDescription')}
      action={
        backHref === undefined ? (
          <AnimatedButton variant="secondary" onClick={onBack}>
            {t('backToFeed')}
          </AnimatedButton>
        ) : (
          <AnimatedButton variant="secondary" asChild>
            <Link href={backHref}>{t('backToFeed')}</Link>
          </AnimatedButton>
        )
      }
    />
  );
}
